import { useState, useRef, useEffect, useCallback } from 'react';

const SWIPE_THRESHOLD = -80;
const LONG_PRESS_MS = 400;
const DIRECTION_LOCK_PX = 8;

function SwipeableProtocolRow({ children, onRemove, onDragStart, onDragMove, onDragEnd, readOnly, isDragging, isDragOver }) {
    const [swipeX, setSwipeX] = useState(0);
    const [animating, setAnimating] = useState(false);
    const contentRef = useRef(null);
    const touchRef = useRef({ x: 0, y: 0 });
    const gestureRef = useRef('none');
    const timerRef = useRef(null);
    const swipeXRef = useRef(0);

    // Keep ref in sync with state for use in native listeners
    swipeXRef.current = swipeX;

    // Store callbacks in refs so native listeners always see latest
    const onRemoveRef = useRef(onRemove);
    const onDragStartRef = useRef(onDragStart);
    const onDragMoveRef = useRef(onDragMove);
    const onDragEndRef = useRef(onDragEnd);
    onRemoveRef.current = onRemove;
    onDragStartRef.current = onDragStart;
    onDragMoveRef.current = onDragMove;
    onDragEndRef.current = onDragEnd;

    useEffect(() => {
        const el = contentRef.current;
        if (!el || readOnly) return;

        const handleTouchStart = (e) => {
            const t = e.touches[0];
            touchRef.current = { x: t.clientX, y: t.clientY };
            gestureRef.current = 'none';
            setAnimating(false);

            if (onDragStartRef.current) {
                timerRef.current = setTimeout(() => {
                    if (gestureRef.current === 'none') {
                        gestureRef.current = 'drag';
                        onDragStartRef.current(e);
                    }
                }, LONG_PRESS_MS);
            }
        };

        const handleTouchMove = (e) => {
            const t = e.touches[0];
            const dx = t.clientX - touchRef.current.x;
            const dy = t.clientY - touchRef.current.y;

            if (gestureRef.current === 'drag') {
                onDragMoveRef.current?.(e);
                if (e.cancelable) e.preventDefault();
                e.stopPropagation();
                return;
            }

            if (gestureRef.current === 'none' && (Math.abs(dx) > DIRECTION_LOCK_PX || Math.abs(dy) > DIRECTION_LOCK_PX)) {
                clearTimeout(timerRef.current);
                gestureRef.current = (Math.abs(dx) > Math.abs(dy) && dx < 0 && onRemoveRef.current) ? 'swipe' : 'scroll';
            }

            if (gestureRef.current === 'swipe') {
                const clamped = Math.min(0, Math.max(dx, -200));
                swipeXRef.current = clamped;
                setSwipeX(clamped);
                if (e.cancelable) e.preventDefault();
                e.stopPropagation();
            }
        };

        const handleTouchEnd = (e) => {
            clearTimeout(timerRef.current);

            if (gestureRef.current === 'drag') {
                onDragEndRef.current?.();
                e.stopPropagation();
                gestureRef.current = 'none';
                return;
            }

            if (gestureRef.current === 'swipe') {
                e.stopPropagation();
                if (swipeXRef.current < SWIPE_THRESHOLD && onRemoveRef.current) {
                    setSwipeX(-window.innerWidth);
                    setAnimating(true);
                    const removeFn = onRemoveRef.current;
                    setTimeout(() => {
                        removeFn();
                        setSwipeX(0);
                        setAnimating(false);
                    }, 250);
                } else {
                    setAnimating(true);
                    setSwipeX(0);
                    setTimeout(() => setAnimating(false), 250);
                }
            }

            gestureRef.current = 'none';
        };

        el.addEventListener('touchstart', handleTouchStart, { passive: true });
        el.addEventListener('touchmove', handleTouchMove, { passive: false });
        el.addEventListener('touchend', handleTouchEnd, { passive: true });

        return () => {
            el.removeEventListener('touchstart', handleTouchStart);
            el.removeEventListener('touchmove', handleTouchMove);
            el.removeEventListener('touchend', handleTouchEnd);
            clearTimeout(timerRef.current);
        };
    }, [readOnly]);

    return (
        <div className={`swipeable-row${isDragging ? ' dragging' : ''}${isDragOver ? ' drag-over' : ''}`}>
            {swipeX < -5 && (
                <div className="swipeable-delete-bg">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 6h18" />
                        <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6" />
                        <path d="M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                    </svg>
                </div>
            )}
            <div
                ref={contentRef}
                className="swipeable-row-content"
                style={{
                    transform: swipeX !== 0 ? `translateX(${swipeX}px)` : undefined,
                    transition: animating ? 'transform 0.25s ease-out' : 'none',
                }}
            >
                {children}
            </div>
        </div>
    );
}

export default SwipeableProtocolRow;
